import React, { useState, useEffect, useCallback } from "react";
import { PlusCircle, Trash2, Save, Upload, Eye, Pencil, X } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VAT_OPTIONS = [
  "Outside Scope of Dutch VAT",
  "0% VAT",
  "9% VAT",
  "21% VAT",
  "Reverse Charge",
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "not_requested", label: "Not Requested" },
  { value: "payment_pending", label: "Payment Pending" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Full Refund" },
];

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-violet-700 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

export default function HotelAccountingPanel({ hotelId, hotel, invoiceNumber, onHotelUpdated }) {
  const { getAuthHeaders } = useAuth();
  const [acc, setAcc] = useState({});
  const [invoice, setInvoice] = useState(null);
  const [saving, setSaving] = useState(null);
  const [newItem, setNewItem] = useState({ description: "", supplier: "", amount: "", currency: "EUR", payment_method: "", reference: "" });
  const [addingItem, setAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItem, setEditItem] = useState({});
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hotels/${hotelId}/accounting`, { headers: getAuthHeaders() });
      setAcc(r.data || {});
    } catch { /**/ }
    try {
      const r2 = await axios.get(`${API}/invoices?source_type=hotel_booking&source_id=${hotelId}`, { headers: getAuthHeaders() });
      const invs = r2.data || [];
      setInvoice(invs.find(i => i.invoice_status !== "cancelled") || null);
    } catch { /**/ }
  }, [hotelId]);

  useEffect(() => { load(); }, [load]);

  // Saves a top-level hotel field (e.g. supplier_name, supplier_reference) via PATCH /hotels/{id}
  const saveHotelField = async (field, value) => {
    setSaving(field);
    try {
      await axios.patch(`${API}/hotels/${hotelId}`, { [field]: value }, { headers: getAuthHeaders() });
      if (onHotelUpdated) onHotelUpdated();
    } catch { toast.error("Save failed"); }
    finally { setSaving(null); }
  };

  const saveField = async (field, value) => {
    setSaving(field);
    try {
      await axios.patch(`${API}/hotels/${hotelId}/accounting`, { [field]: value }, { headers: getAuthHeaders() });
      setAcc(prev => ({ ...prev, [field]: value }));
    } finally {
      setSaving(null);
    }
  };

  const handleBlur = (field, value) => saveField(field, value);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        const ext = file.name.split(".").pop() || "pdf";
        const res = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64,
          filename: `hotel-${hotelId}.${ext}`,
          mime_type: file.type,
          folder: "supplier-receipts",
          invoice_number: invoiceNumber || hotelId,
        }, { headers: getAuthHeaders() });
        if (res.data?.url) {
          await saveField("supplier_receipt_url", res.data.url);
          setAcc(prev => ({ ...prev, supplier_receipt_url: res.data.url }));
          toast.success("Receipt uploaded ✓");
          load();
        }
      } catch { toast.error("Upload failed"); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteReceipt = async () => {
    if (!window.confirm("Remove this supplier invoice?")) return;
    try {
      await axios.delete(`${API}/hotels/${hotelId}/accounting/supplier-invoice`, { headers: getAuthHeaders() });
      setAcc(prev => ({ ...prev, supplier_receipt_url: undefined }));
      toast.success("Receipt removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const addCostItem = async () => {
    if (!newItem.description || !newItem.amount) return;
    try {
      await axios.post(`${API}/hotels/${hotelId}/accounting/cost-items`, {
        ...newItem,
        amount: parseFloat(newItem.amount) || 0,
      }, { headers: getAuthHeaders() });
      setNewItem({ description: "", supplier: "", amount: "", currency: "EUR", payment_method: "", reference: "" });
      setAddingItem(false);
      load();
    } catch { alert("Failed to add cost item"); }
  };

  const deleteCostItem = async (itemId) => {
    if (!window.confirm("Remove this cost item?")) return;
    await axios.delete(`${API}/hotels/${hotelId}/accounting/cost-items/${itemId}`, { headers: getAuthHeaders() });
    load();
  };

  const startEdit = (item) => {
    setEditingItemId(item.id);
    setEditItem({
      description:    item.description    || "",
      supplier:       item.supplier       || "",
      amount:         item.amount != null ? String(item.amount) : "",
      currency:       item.currency       || "EUR",
      payment_method: item.payment_method || "",
      reference:      item.reference      || "",
    });
    setAddingItem(false);
  };

  const cancelEdit = () => { setEditingItemId(null); setEditItem({}); };

  const updateCostItem = async (itemId) => {
    const amount = parseFloat(editItem.amount);
    if (!editItem.description.trim()) { toast.error("Description is required"); return; }
    if (isNaN(amount) || editItem.amount === "") { toast.error("A valid amount is required"); return; }
    try {
      await axios.patch(
        `${API}/hotels/${hotelId}/accounting/cost-items/${itemId}`,
        { ...editItem, amount },
        { headers: getAuthHeaders() }
      );
      cancelEdit();
      load();
      toast.success("Cost item updated ✓");
    } catch { toast.error("Update failed"); }
  };

  const costItems = acc.cost_items || [];
  const totalCost = costItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const amountReceived = parseFloat(acc.amount_received) || 0;
  const bankFee = parseFloat(acc.bank_fee) || 0;
  const grossProfit = amountReceived ? round2(amountReceived - totalCost) : null;
  const netProfit   = grossProfit !== null ? round2(grossProfit - bankFee) : null;

  return (
    <div className="space-y-6">
      {/* Accounting details */}
      <div className="bg-white border border-violet-100 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Accounting Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Supplier Name">
            <input
              type="text"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Traveluro, Booking.com"
              defaultValue={hotel?.supplier_name || ""}
              key={hotel?.supplier_name}
              onBlur={e => { if (e.target.value !== (hotel?.supplier_name || "")) saveHotelField("supplier_name", e.target.value); }}
            />
          </Field>
          <Field label="Supplier Reference #">
            <input
              type="text"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono"
              placeholder="e.g. ABC123"
              defaultValue={hotel?.supplier_reference || ""}
              key={hotel?.supplier_reference}
              onBlur={e => { if (e.target.value !== (hotel?.supplier_reference || "")) saveHotelField("supplier_reference", e.target.value); }}
            />
          </Field>
          <Field label="Invoice Date">
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              defaultValue={acc.invoice_date || ""}
              onBlur={e => handleBlur("invoice_date", e.target.value)}
            />
          </Field>
          <Field label="Quarter">
            <input
              type="text"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Q3-2025"
              defaultValue={acc.quarter || ""}
              onBlur={e => handleBlur("quarter", e.target.value)}
            />
          </Field>
          <Field label="VAT Type">
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={acc.vat_type || "Outside Scope of Dutch VAT"}
              onChange={e => saveField("vat_type", e.target.value)}
            >
              {VAT_OPTIONS.map(v => <option key={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Bank Fee (€)">
            <input
              type="number"
              step="0.01"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              defaultValue={acc.bank_fee || ""}
              onBlur={e => handleBlur("bank_fee", parseFloat(e.target.value) || 0)}
            />
          </Field>
          <Field label="Comment">
            <input
              type="text"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              defaultValue={acc.comment || ""}
              onBlur={e => handleBlur("comment", e.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* Customer payment */}
      <div className="bg-white border border-violet-100 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Customer Payment</h3>
        {invoice && (
          <p className="text-xs text-violet-600 mb-3 bg-violet-50 px-3 py-2 rounded-lg">
            Linked to invoice <strong>{invoice.invoice_number}</strong> — financial fields sync both ways.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Payment Status">
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={acc.payment_status || "not_requested"}
              onChange={e => saveField("payment_status", e.target.value)}
            >
              {PAYMENT_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Amount Received (€)">
            <input
              type="number"
              step="0.01"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              defaultValue={acc.amount_received || ""}
              onBlur={e => handleBlur("amount_received", parseFloat(e.target.value) || 0)}
            />
          </Field>
          <Field label="Date Received">
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              defaultValue={acc.date_received || ""}
              onBlur={e => handleBlur("date_received", e.target.value)}
            />
          </Field>
          <Field label="Payment Mode">
            <input
              type="text"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Tikkie, Bank transfer"
              defaultValue={acc.payment_mode || ""}
              onBlur={e => handleBlur("payment_mode", e.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* Supplier cost items */}
      <div className="bg-white border border-violet-100 rounded-xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider">Supplier Cost Items</h3>
          <button
            onClick={() => setAddingItem(true)}
            className="flex items-center gap-1 text-xs text-violet-700 hover:text-violet-900 font-semibold"
          >
            <PlusCircle size={14} /> Add Item
          </button>
        </div>

        {costItems.length === 0 && !addingItem && (
          <p className="text-sm text-gray-400 italic">No cost items yet.</p>
        )}

        <div className="space-y-2">
          {costItems.map(item => (
            <div key={item.id}>
              {editingItemId === item.id ? (
                /* ── Inline edit form ── */
                <div className="border border-violet-200 rounded-lg p-4 space-y-3 bg-violet-50">
                  <h4 className="text-xs font-bold text-violet-800 uppercase tracking-wide">Edit Cost Item</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" placeholder="Description *" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={editItem.description} onChange={e => setEditItem(p => ({ ...p, description: e.target.value }))} />
                    <input type="text" placeholder="Supplier" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={editItem.supplier} onChange={e => setEditItem(p => ({ ...p, supplier: e.target.value }))} />
                    <input type="number" placeholder="Amount *" step="0.01" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={editItem.amount} onChange={e => setEditItem(p => ({ ...p, amount: e.target.value }))} />
                    <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={editItem.currency} onChange={e => setEditItem(p => ({ ...p, currency: e.target.value }))}>
                      {["EUR","USD","GBP","AED","SAR"].map(c => <option key={c}>{c}</option>)}
                    </select>
                    <input type="text" placeholder="Via / Payment account" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={editItem.payment_method} onChange={e => setEditItem(p => ({ ...p, payment_method: e.target.value }))} />
                    <input type="text" placeholder="Supplier reference" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      value={editItem.reference} onChange={e => setEditItem(p => ({ ...p, reference: e.target.value }))} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateCostItem(item.id)}
                      className="flex items-center gap-1 bg-violet-700 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-violet-800">
                      <Save size={14} /> Save
                    </button>
                    <button onClick={cancelEdit} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 px-3 py-2">
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Read-only row ── */
                <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{item.description}</p>
                    {item.supplier && <p className="text-xs text-gray-500">Supplier: {item.supplier}</p>}
                    {item.payment_method && <p className="text-xs text-gray-500">Via: {item.payment_method}</p>}
                    {item.reference && <p className="text-xs text-gray-500">Ref: {item.reference}</p>}
                    {item.paid_date && <p className="text-xs text-gray-500">Paid: {item.paid_date}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-gray-900">€{parseFloat(item.amount || 0).toFixed(2)}</p>
                    <p className="text-xs text-gray-400">{item.currency || "EUR"}</p>
                  </div>
                  <button onClick={() => startEdit(item)} className="text-violet-400 hover:text-violet-700 shrink-0" title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deleteCostItem(item.id)} className="text-red-400 hover:text-red-600 shrink-0" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {addingItem && (
          <div className="mt-3 border border-violet-200 rounded-lg p-4 space-y-3 bg-violet-50">
            <h4 className="text-xs font-bold text-violet-800 uppercase tracking-wide">New Cost Item</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text" placeholder="Description *" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))} />
              <input type="text" placeholder="Supplier" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={newItem.supplier} onChange={e => setNewItem(p => ({ ...p, supplier: e.target.value }))} />
              <input type="number" placeholder="Amount *" step="0.01" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={newItem.amount} onChange={e => setNewItem(p => ({ ...p, amount: e.target.value }))} />
              <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={newItem.currency} onChange={e => setNewItem(p => ({ ...p, currency: e.target.value }))}>
                {["EUR","USD","GBP","AED","SAR"].map(c => <option key={c}>{c}</option>)}
              </select>
              <input type="text" placeholder="Payment method" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={newItem.payment_method} onChange={e => setNewItem(p => ({ ...p, payment_method: e.target.value }))} />
              <input type="text" placeholder="Reference" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={newItem.reference} onChange={e => setNewItem(p => ({ ...p, reference: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <button onClick={addCostItem}
                className="flex items-center gap-1 bg-violet-700 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-violet-800">
                <Save size={14} /> Save
              </button>
              <button onClick={() => setAddingItem(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2">Cancel</button>
            </div>
          </div>
        )}

        {costItems.length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total Supplier Cost</span>
              <span className="font-bold text-gray-900">€{totalCost.toFixed(2)}</span>
            </div>
            {amountReceived > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount Received</span>
                <span className="font-bold text-gray-900">€{amountReceived.toFixed(2)}</span>
              </div>
            )}
            {grossProfit !== null && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Gross Profit</span>
                <span className={grossProfit >= 0 ? "text-green-700 font-semibold" : "text-red-600 font-semibold"}>€{grossProfit.toFixed(2)}</span>
              </div>
            )}
            {netProfit !== null && (
              <div className="flex justify-between text-sm font-bold">
                <span className="text-violet-700">Net Profit</span>
                <span className={netProfit >= 0 ? "text-green-700" : "text-red-600"}>€{netProfit.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Supplier payment */}
      <div className="bg-white border border-violet-100 rounded-xl p-5 shadow-sm">
        <h3 className="text-sm font-bold text-violet-800 uppercase tracking-wider mb-4">Supplier Payment</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Supplier Payment Status">
            <select
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={acc.supplier_payment_status || "unpaid"}
              onChange={e => saveField("supplier_payment_status", e.target.value)}
            >
              {["unpaid","partially_paid","paid"].map(v => <option key={v} value={v}>{v.replace("_"," ")}</option>)}
            </select>
          </Field>
          <Field label="Supplier Paid Date">
            <input
              type="date"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
              defaultValue={acc.supplier_paid_date || ""}
              onBlur={e => handleBlur("supplier_paid_date", e.target.value)}
            />
          </Field>
        </div>

        {/* Supplier Invoice / Proof — internal only, never shown on customer voucher */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-2">Supplier Invoice / Proof</p>
          {acc.supplier_receipt_url ? (
            <div className="flex items-center gap-2 flex-wrap">
              <a href={acc.supplier_receipt_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 bg-blue-50 rounded-lg px-3 py-2">
                <Eye size={14} /> View Invoice
              </a>
              <label className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-800 border border-violet-200 bg-violet-50 rounded-lg px-3 py-2 cursor-pointer">
                <Upload size={14} />
                {uploading ? "Uploading…" : "Replace"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleReceiptUpload} disabled={uploading} />
              </label>
              <button onClick={handleDeleteReceipt}
                className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 border border-red-200 bg-red-50 rounded-lg px-3 py-2">
                <Trash2 size={14} /> Remove
              </button>
            </div>
          ) : (
            <label className="flex items-center gap-2 text-sm text-gray-500 hover:text-violet-700 border border-dashed border-gray-300 hover:border-violet-400 rounded-lg px-3 py-2 cursor-pointer transition-colors w-fit">
              <Upload size={14} />
              {uploading ? "Uploading…" : "Upload Invoice / Proof (PDF or image, max 10MB)"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleReceiptUpload} disabled={uploading} />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
